import { List } from 'immutable';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { proofOfPerformanceReportSelector } from '@sophocles/entities/ProofOfPerformanceReport';
import { selectSiteUnit } from '@sophocles/features/entities/site';
import { selectAlarmDefinitions } from '@sophocles/features/reference';
import { toReadableTime } from '@sophocles/utils';

export const createPopList = (t, tAlarm, siteId) =>
  createSelector(
    selectSiteUnit(siteId),
    proofOfPerformanceReportSelector,
    selectAlarmDefinitions,
    (unit, pops, alarmDefinitions) => {
      if (!unit) {
        return new List();
      }
      const alarmKeyById = new Map(alarmDefinitions.map(definition => [String(definition.id), definition.key]));
      pops = pops.toList().map(x => x.toMap()); // convert pops to List of Maps instead of Map of Records
      const relays = unit.relays ?? [];
      // id -> relay_number lookup so assoc_relay_number can resolve a relay's
      // secondary_relay_id (a scalar id, not a nested object) to a display number.
      const relayNumberById = relays.reduce((acc, relay) => {
        acc[relay.id] = relay.relay_number;
        return acc;
      }, {});
      relays.forEach(relay => {
        const loads = relay.loads ?? [];
        loads.forEach(load => {
          pops = pops.map(pop => {
            if (pop.get('relay_load', -1) == load.id) {
              const popAlarms = pop.get('alarms', []); // just array of ints
              const displayAlarms =
                popAlarms.length > 0
                  ? _.map(popAlarms, alarm => tAlarm(`alarms.${alarmKeyById.get(alarm.toString()) ?? 'unknown'}`)).join(
                      ', ',
                    )
                  : null;
              pop = pop.set('site_id', siteId); // site should always have id
              pop = pop.set('relay_load_name', load.name ?? t('sites.noRelayLoad', 'NO RELAY LOAD '));
              pop = pop.set('relay_number', relay.relay_number ?? 0);
              pop = pop.set(
                'alarm_display',
                displayAlarms != null ? displayAlarms : pop.get('legacy_alarm_string', ''),
              );
              const assocRelayNumber = relayNumberById[relay.secondary_relay_id];
              if (assocRelayNumber != null) {
                pop = pop.set('assoc_relay_number', assocRelayNumber);
              }
            }
            return pop;
          });
        });
      });
      // now take out the ones with bad site_id
      pops = pops.filter(pop => pop.get('site_id', -1) != -1);
      pops = pops.map(pop => {
        const schedules = [];
        for (let i = 1; i < 5; i++) {
          if (pop.get(`schedule_${i}_on`, null) != null) {
            schedules.push(
              toReadableTime(pop.get(`schedule_${i}_on`)) + ' - ' + toReadableTime(pop.get(`schedule_${i}_off`)),
            );
          }
        }
        pop = pop.set(
          'schedules',
          pop.get('status_received', false)
            ? pop.get('always_on', false)
              ? t('sites.alwaysOn')
              : schedules.length > 0
                ? schedules.join(', ')
                : t('sites.noActiveSchedules')
            : t('sites.unitDidNotReportIn'),
        );
        return pop.set('passFail', pop.get('passed') ? 'PASS' : 'FAIL');
      });

      // default sort
      pops = pops.sort(dateRelayPanelComparator);

      return pops;
    },
  );

function dateRelayPanelComparator(a, b) {
  if (a.get('report_date') > b.get('report_date')) {
    return -1;
  }
  if (a.get('report_date') < b.get('report_date')) {
    return 1;
  }

  return relayPanelComparator(a, b);
}

function relayPanelComparator(a, b) {
  if (a.get('relay_number') < b.get('relay_number')) {
    return 1;
  }
  if (a.get('relay_number') > b.get('relay_number')) {
    return -1;
  }

  return panelComparator(a, b);
}

function panelComparator(a, b) {
  if (a.get('relay_load_name') < b.get('relay_load_name')) {
    return 1;
  }
  return -1;
}
